import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";

// ─── Airport JSON → country suggestions (GET /admin/countries/search) ─────────
async function fetchCountrySuggestions(q) {
  const trimmed = String(q || "").trim();
  if (!trimmed) return [];
  try {
    const res = await api.get("/admin/countries/search", { params: { q: trimmed } });
    const data = res.data?.data;
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

function useCountrySuggestions(query, { minChars = 1 } = {}) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const q = String(query || "").trim();
    if (q.length < minChars) {
      setItems([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      const list = await fetchCountrySuggestions(q);
      if (!cancelled) setItems(list);
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, minChars]);
  return items;
}

function CountrySuggestDropdown({ items, onPick }) {
  if (!items.length) return null;
  return (
    <ul className="absolute left-0 right-0 top-full mt-1 max-h-48 overflow-auto rounded-xl border bg-white shadow-lg z-50 text-sm">
      {items.map((c) => (
        <li key={c.code}>
          <button
            type="button"
            className="w-full text-left px-3 py-2 hover:bg-gray-100"
            onMouseDown={(e) => {
              e.preventDefault();
              onPick(c);
            }}
          >
            <span className="font-medium">{c.name}</span>{" "}
            <span className="text-gray-500">({c.code})</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

async function fetchExactCountryMatch(raw) {
  const t = String(raw || "").trim();
  if (!t) return null;
  const list = await fetchCountrySuggestions(t);
  const lower = t.toLowerCase();
  return (
    list.find(
      (x) =>
        String(x.name).toLowerCase() === lower ||
        String(x.code).toLowerCase() === lower
    ) || null
  );
}

/** LayOver Country + LayOver Country Code — bidirectional autocomplete */
function LayoverCountrySuggestFields({ form, setForm }) {
  const [focusField, setFocusField] = useState(null);
  const sugCountry = useCountrySuggestions(form.country, { minChars: 1 });
  const sugCode = useCountrySuggestions(form.country_code, { minChars: 1 });

  const applyPick = (c) => {
    setForm((s) => ({
      ...s,
      country: c.name,
      country_code: String(c.code).toUpperCase(),
    }));
  };

  const scheduleBlur = () => {
    setTimeout(() => setFocusField(null), 180);
  };

  return (
    <>
      <div>
        <label className="text-sm font-semibold">
          LayOver Country <span className="text-red-500">*</span>
        </label>
        <div className="relative mt-2">
          <input
            className="w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="e.g. United Kingdom"
            value={form.country}
            onChange={(e) => setForm((s) => ({ ...s, country: e.target.value }))}
            onFocus={() => setFocusField("country")}
            onBlur={scheduleBlur}
          />
          {focusField === "country" && (
            <CountrySuggestDropdown items={sugCountry} onPick={applyPick} />
          )}
        </div>
      </div>

      <div>
        <label className="text-sm font-semibold">
          LayOver Country Code <span className="text-red-500">*</span>
        </label>
        <div className="relative mt-2">
          <input
            className="w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black uppercase"
            placeholder="e.g. GB"
            maxLength={10}
            value={form.country_code}
            onChange={(e) =>
              setForm((s) => ({
                ...s,
                country_code: e.target.value.toUpperCase(),
              }))
            }
            onFocus={() => setFocusField("code")}
            onBlur={scheduleBlur}
          />
          {focusField === "code" && (
            <CountrySuggestDropdown items={sugCode} onPick={applyPick} />
          )}
        </div>
      </div>
    </>
  );
}

/** Exemption names + codes — add/remove pairs; typing shows filtered suggestions */
function PairedExemptionCountryTags({ countries, codes, setForm }) {
  const [nameInput, setNameInput] = useState("");
  const [codeInput, setCodeInput] = useState("");
  const [focusRow, setFocusRow] = useState(null);

  const nameSug = useCountrySuggestions(nameInput, { minChars: 1 });
  const codeSug = useCountrySuggestions(codeInput, { minChars: 1 });

  const scheduleBlur = () => {
    setTimeout(() => setFocusRow(null), 180);
  };

  const addPair = (name, code) => {
    const u = String(code).trim().toUpperCase();
    const n = String(name).trim();
    if (!n || !u) return;
    setForm((s) => {
      const cc = s.exemption_country_codes;
      if (cc.some((x) => String(x).toUpperCase() === u)) return s;
      return {
        ...s,
        exemption_countries: [...s.exemption_countries, n],
        exemption_country_codes: [...cc, u],
      };
    });
  };

  const removeAt = (index) => {
    setForm((s) => ({
      ...s,
      exemption_countries: s.exemption_countries.filter((_, i) => i !== index),
      exemption_country_codes: s.exemption_country_codes.filter((_, i) => i !== index),
    }));
  };

  const pickFromSuggestion = (c, row) => {
    addPair(c.name, c.code);
    if (row === "name") setNameInput("");
    else setCodeInput("");
  };

  const flushNameInput = async () => {
    const t = nameInput.trim();
    if (!t) return;
    const exact = await fetchExactCountryMatch(t);
    if (exact) addPair(exact.name, exact.code);
    else {
      setForm((s) => ({
        ...s,
        exemption_countries: [...s.exemption_countries, t],
      }));
    }
    setNameInput("");
  };

  const flushCodeInput = async () => {
    const t = codeInput.trim();
    if (!t) return;
    const exact = await fetchExactCountryMatch(t);
    if (exact) addPair(exact.name, exact.code);
    else {
      setForm((s) => ({
        ...s,
        exemption_country_codes: [...s.exemption_country_codes, t.toUpperCase()],
      }));
    }
    setCodeInput("");
  };

  const onNameKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      flushNameInput();
    }
    if (e.key === "Backspace" && !nameInput && countries.length > 0) {
      removeAt(countries.length - 1);
    }
  };

  const onCodeKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      flushCodeInput();
    }
    if (e.key === "Backspace" && !codeInput && codes.length > 0) {
      removeAt(codes.length - 1);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="text-sm font-semibold">
          Exemption If Holding Valid Visa From (Destination Country Names)
        </label>
        <p className="text-xs text-gray-500 mt-0.5">
          Type to filter by country name or code from the airport list. Pick a row or press{" "}
          <kbd className="border rounded px-1">Enter</kbd> — matching pairs fill both fields.
        </p>
        <div className="relative mt-2">
          <div
            className="min-h-[44px] flex flex-wrap gap-1.5 items-center border rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-black cursor-text"
            onClick={() => document.getElementById("exemption-name-input")?.focus()}
          >
            {countries.map((tag, i) => (
              <span
                key={`n-${i}-${tag}`}
                className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium"
              >
                {tag}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeAt(i);
                  }}
                  className="text-gray-400 hover:text-red-500 font-bold leading-none"
                >
                  ×
                </button>
              </span>
            ))}
            <input
              id="exemption-name-input"
              className="flex-1 min-w-[120px] outline-none text-sm bg-transparent"
              placeholder={countries.length === 0 ? "e.g. Canada, India..." : ""}
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={onNameKeyDown}
              onFocus={() => setFocusRow("name")}
              onBlur={() => {
                scheduleBlur();
                if (nameInput.trim()) flushNameInput();
              }}
            />
          </div>
          {focusRow === "name" && (
            <CountrySuggestDropdown
              items={nameSug}
              onPick={(c) => pickFromSuggestion(c, "name")}
            />
          )}
        </div>
      </div>

      <div>
        <label className="text-sm font-semibold">Exemption Destination Country Codes</label>
        <p className="text-xs text-gray-500 mt-0.5">
          Type to filter — selecting a suggestion adds the matching country name above.
        </p>
        <div className="relative mt-2">
          <div
            className="min-h-[44px] flex flex-wrap gap-1.5 items-center border rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-black cursor-text"
            onClick={() => document.getElementById("exemption-code-input")?.focus()}
          >
            {codes.map((tag, i) => (
              <span
                key={`c-${i}-${tag}`}
                className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium uppercase"
              >
                {tag}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeAt(i);
                  }}
                  className="text-gray-400 hover:text-red-500 font-bold leading-none"
                >
                  ×
                </button>
              </span>
            ))}
            <input
              id="exemption-code-input"
              className="flex-1 min-w-[120px] outline-none text-sm bg-transparent uppercase"
              placeholder={codes.length === 0 ? "e.g. CA, IN, US..." : ""}
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
              onKeyDown={onCodeKeyDown}
              onFocus={() => setFocusRow("code")}
              onBlur={() => {
                scheduleBlur();
                if (codeInput.trim()) flushCodeInput();
              }}
            />
          </div>
          {focusRow === "code" && (
            <CountrySuggestDropdown
              items={codeSug}
              onPick={(c) => pickFromSuggestion(c, "code")}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Preview card ─────────────────────────────────────────────────────────────
function TransitVisaPreviewCard({ form }) {
  return (
    <div className="border rounded-2xl overflow-hidden shadow-sm bg-white p-5 space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex flex-col items-center justify-center bg-gray-800 text-white rounded-xl w-16 h-16 shrink-0">
          <span className="text-lg font-bold leading-none">
            {(form.country_code || "??").toUpperCase()}
          </span>
          <span className="text-[9px] mt-0.5 opacity-70">CODE</span>
        </div>

        <div>
          <h2 className="text-xl font-bold">
            {form.country || <span className="text-gray-400">Country Name</span>}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">Transit Visa Requirement</p>
        </div>
      </div>

      {form.when_required && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            When Required
          </p>
          <p className="text-sm text-gray-700 leading-relaxed">{form.when_required}</p>
        </div>
      )}

      {(form.exemption_countries.length > 0 || form.exemption_country_codes.length > 0) && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Exempt If Holding Valid Visa From
          </p>

          {form.exemption_countries.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {form.exemption_countries.map((c, i) => (
                <span
                  key={i}
                  className="inline-flex items-center rounded-full bg-green-50 border border-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700"
                >
                  {c}
                </span>
              ))}
            </div>
          )}

          {form.exemption_country_codes.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {form.exemption_country_codes.map((c, i) => (
                <span
                  key={i}
                  className="inline-flex items-center rounded-full bg-blue-50 border border-blue-100 px-2 py-0.5 text-xs font-bold text-blue-700"
                >
                  {c}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {form.notes && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Notes
          </p>
          <p className="text-sm text-gray-700 leading-relaxed">{form.notes}</p>
        </div>
      )}

      {!form.country && !form.when_required && !form.notes && form.exemption_countries.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-4">
          Fill in the form to see a preview
        </p>
      )}
    </div>
  );
}

// ─── Default form state ───────────────────────────────────────────────────────
const DEFAULT_FORM = {
  country: "",
  country_code: "",
  when_required: "",
  exemption_countries: [], // string[]
  exemption_country_codes: [], // string[]
  notes: "",
};

// ─── Main page ────────────────────────────────────────────────────────────────
export default function TransitVisaUpsertPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  // Load existing record when editing
  useEffect(() => {
    if (!isEdit) return;

    const load = async () => {
      try {
        const res = await api.get(`/admin/transit-visa-requirements/${id}`);
        const d = res.data?.data;
        if (!d) return;

        setForm({
          country: d.country || "",
          country_code: d.country_code || "",
          when_required: d.when_required || "",
          exemption_countries: Array.isArray(d.exemption_countries) ? d.exemption_countries : [],
          exemption_country_codes: Array.isArray(d.exemption_country_codes)
            ? d.exemption_country_codes
            : [],
          notes: d.notes || "",
        });
      } catch (e) {
        toast.error("Failed to load record");
      }
    };

    load();
  }, [id, isEdit]);

  const onChange = (key) => (e) =>
    setForm((s) => ({
      ...s,
      [key]: key === "country_code" ? e.target.value.toUpperCase() : e.target.value,
    }));

  const validate = () => {
    if (!form.country.trim()) return "Country is required";
    if (!form.country_code.trim()) return "Country Code is required";
    if (!form.when_required.trim()) return "When Required is required";
    if (!form.notes.trim()) return "Notes is required";

    const { exemption_countries, exemption_country_codes } = form;
    if (
      exemption_countries.length > 0 &&
      exemption_country_codes.length > 0 &&
      exemption_countries.length !== exemption_country_codes.length
    ) {
      return `Exemption countries (${exemption_countries.length}) and exemption country codes (${exemption_country_codes.length}) must have the same count`;
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    const err = validate();
    if (err) return toast.error(err);

    const payload = {
      country: form.country.trim(),
      country_code: form.country_code.trim().toUpperCase(),
      when_required: form.when_required.trim(),
      exemption_countries: form.exemption_countries,
      exemption_country_codes: form.exemption_country_codes,
      notes: form.notes.trim(),
    };

    setSaving(true);
    try {
      if (!isEdit) {
        await api.post("/admin/transit-visa-requirements", payload);
        toast.success("Record created");
      } else {
        await api.put(`/admin/transit-visa-requirements/${id}`, payload);
        toast.success("Record updated");
      }
      navigate("/admin/transit-visa-requirements");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold">
          {isEdit ? "Edit Transit Visa Requirement" : "Add Transit Visa Requirement"}
        </h1>
        <button
          type="button"
          onClick={() => navigate("/admin/transit-visa-requirements")}
          className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50 self-start"
        >
          Back
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
        {/* ── Form ── */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <LayoverCountrySuggestFields form={form} setForm={setForm} />

          {/* When Required */}
          <div>
            <label className="text-sm font-semibold">
              When Required <span className="text-red-500">*</span>
            </label>
            <textarea
              className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black min-h-[100px] resize-none"
              placeholder="Describe when a transit visa is required..."
              value={form.when_required}
              onChange={onChange("when_required")}
            />
          </div>

          <PairedExemptionCountryTags
            countries={form.exemption_countries}
            codes={form.exemption_country_codes}
            setForm={setForm}
          />

          {/* Pair count warning */}
          {form.exemption_countries.length > 0 &&
            form.exemption_country_codes.length > 0 &&
            form.exemption_countries.length !== form.exemption_country_codes.length && (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                ⚠️ Count mismatch — exemption country names ({form.exemption_countries.length}) and
                codes ({form.exemption_country_codes.length}) should match.
              </p>
            )}

          {/* Notes */}
          <div>
            <label className="text-sm font-semibold">
              Notes <span className="text-red-500">*</span>
            </label>
            <textarea
              className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black min-h-[80px] resize-none"
              placeholder="Additional notes or exceptions..."
              value={form.notes}
              onChange={onChange("notes")}
            />
          </div>

          {/* Actions */}
          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => navigate("/admin/transit-visa-requirements")}
              className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold disabled:opacity-60"
            >
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create"}
            </button>
          </div>
        </form>

        {/* ── Preview ── */}
        {/* <div>
          <h2 className="text-lg font-bold mb-3">Preview</h2>
          <TransitVisaPreviewCard form={form} />
        </div> */}
      </div>
    </div>
  );
}
